// src/components/ReviewsForm/ReviewsForm.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { Grid } from '@mui/material';
import { Rating } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { getNavigateToLoginOptions } from '../../utils/authSessionExpired';
import { useAuth } from '../../context/AuthContext';
import { reviewsService } from '../../services/api/reviewsService';
import NkySnackbarAlert from '../common/NkySnackbarAlert/NkySnackbarAlert';
import NkySpinner from '../common/NkySpinner/NkySpinner';

import {
    Wrapper,
    Heading,
    Subheading,
    FormContainer,
    StyledTextField,
    SubmitButton,
    RatingRow,
    RatingLabel,
} from './ReviewsForm.styled';

// TODO: create a reviewsService.createReview(...) in your api layer.
// For now, leave the POST call as a comment in handleSubmit.

const MAX_CHARS = 200;

export default function ReviewsForm({
    businessId,
    businessName,
    onSubmitted, // optional callback for parent to refetch reviews
}) {
    const navigate = useNavigate();
    const location = useLocation();
    const { user, loading } = useAuth();

    const [formData, setFormData] = useState({
        rating: 0,
        reviewText: '',
    });

    const [errors, setErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [snackbar, setSnackbar] = useState({
        open: false,
        severity: 'info',
        message: '',
    });

    useEffect(() => {
        if (loading) return;
        if (!user) navigate('/login', getNavigateToLoginOptions(location));
    }, [user, loading, navigate, location]);

    const canSubmit = useMemo(() => !!businessId && !isLoading, [businessId, isLoading]);

    const openSnack = (severity, message) =>
        setSnackbar({ open: true, severity, message });

    const validateForm = () => {
        const newErrors = {};
        const ratingNum = Number(formData.rating || 0);

        if (!businessId) newErrors.businessId = 'Missing business id.';
        if (ratingNum < 1) newErrors.rating = 'Rating is required.';
        if (formData.reviewText.length > MAX_CHARS) {
            newErrors.reviewText = `Maximum ${MAX_CHARS} characters allowed.`;
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleTextChange = (e) => {
        const val = e.target.value;
        setFormData((prev) => ({ ...prev, reviewText: val }));
    };

    const handleRatingChange = (_, newValue) => {
        setFormData((prev) => ({ ...prev, rating: newValue || 0 }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        setIsLoading(true);

        const payload = {
            Rating: Number(formData.rating),
            ReviewText: formData.reviewText.trim() || null,
        };

        try {
            // ✅ POST the review
            const response = await reviewsService.createReview(businessId, payload);
            const status = response.status;

            if (status === 201) {
                openSnack('success', 'Thanks! Your review was submitted.');
                setFormData({ rating: 0, reviewText: '' });
                setErrors({});
                onSubmitted?.();
            } else if (status === 401) {
                openSnack('info', 'Please log in to leave a review.');
                navigate('/login', getNavigateToLoginOptions(location));
            } else if (status === 409) {
                openSnack('info', 'You already left a review for this business.');
            } else if (status === 500) {
                openSnack(
                    'error',
                    'Something went wrong. Please check back later or contact support@nkyconnect.com',
                );
            } else {
                openSnack('error', 'Unexpected response from the server.');
            }
        } catch (err) {
            openSnack(
                'error',
                'Something went wrong. Please check back later or contact support@nkyconnect.com',
            );
        } finally {
            setIsLoading(false);
        }
    };


    return (
        <Wrapper style={{ position: 'relative' }}>
            {/* Overlay Loader */}
            {isLoading && (
                <div
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                        zIndex: 9999,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <NkySpinner size="lg" />
                </div>
            )}

            <Heading>Leave a Review</Heading>
            <Subheading>
                {businessName
                    ? `Tell others about your experience with ${businessName}.`
                    : 'Tell others about your experience.'}
            </Subheading>

            <FormContainer component="form" onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <RatingRow>
                            <RatingLabel>Rating *</RatingLabel>
                            <Rating
                                value={Number(formData.rating)}
                                onChange={handleRatingChange}
                                size="large"
                            />
                        </RatingRow>
                        {errors.rating && (
                            <div style={{ marginTop: 8, color: '#d32f2f', fontSize: 13 }}>
                                {errors.rating}
                            </div>
                        )}
                    </Grid>

                    <Grid item xs={12}>
                        <StyledTextField
                            name="reviewText"
                            label="Write a review (optional)"
                            placeholder="Share details like punctuality, quality, communication, and overall satisfaction."
                            value={formData.reviewText}
                            onChange={handleTextChange}
                            error={!!errors.reviewText}
                            helperText={errors.reviewText || `${formData.reviewText.length}/${MAX_CHARS} characters`}
                            inputProps={{ maxLength: MAX_CHARS }}
                            multiline
                            minRows={4}
                            fullWidth
                        />
                    </Grid>
                </Grid>

                <SubmitButton type="submit" disabled={!canSubmit}>
                    Submit Review
                </SubmitButton>
            </FormContainer>

            <NkySnackbarAlert
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
                severity={snackbar.severity}
                message={snackbar.message}
            />
        </Wrapper>
    );
}
